import { ButtonHTMLAttributes } from "react";
import clsx from "clsx";
import LoaderOnButton from "../common/LoaderOnButton";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: "primary" | "secondary" | "warning" | "error" | "default" | "inherit" | "link";
  variant?: string;
  isLoading?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large"
}

export function Button({
  color = "primary",
  variant = "contained",
  className,
  type = "button",
  disabled = false,
  isLoading = false,
  fullWidth = false,
  size="medium",
  ...rest
}: ButtonProps) {
  const baseCls = clsx(
    // button size
    size === "small" && "text-sm",
    ["medium", "large"].includes(size) && "text-base",
    "rounded-md",
    "sm:rounded-lg",
    "rounded-tr-none",
    "sm:rounded-tr-none",
    "font-semibold",
    // btn variant type
    variant === "contained" && [
      "shadow-md",
      color === "primary" && "bg-primary",
      color === "secondary" && "bg-secondary",
      color === "warning" && "bg-warning",
      color === "error" && "bg-error",
      color === "default" && "bg-default",
      "text-white",
      "dark:text-gray-800",
      "hover:brightness-90",
      // padding based btn size
      size === "small" && "px-4 py-2",
      size === "medium" && "px-6 py-3",
      size === "large" && "px-6 py-4",    
    ],
    variant === "outlined" && [
      "bg-inherit border",
      color === "primary" && "border-primary text-primary",
      color === "secondary" && "border-secondary text-secondary",
      color === "warning" && "border-warning text-warning",
      color === "error" && "border-error text-error",
      color === "default" && "border-gray-800 hover:bg-gray-100 dark:border-white dark:hover:bg-inherit",
      color === "inherit" && "hover:bg-gray-100 dark:border-white dark:hover:bg-inherit",
      // padding based btn size
      size === "small" && "px-4 py-2",
      size === "medium" && "px-6 py-3",
      size === "large" && "px-8 py-4",    
    ],
    variant === "text" && [
      color === "primary" && "text-primary",
      color === "secondary" && "text-secondary",
      color === "warning" && "text-warning",
      color === "error" && "text-error",
      color === "default" && "text-default",
      "hover:brightness-70",
      "dark:text-white",
      "px-3 py-2"
    ],
    variant === "link" && [
      color === "primary" && "text-primary",
      color === "secondary" && "text-secondary",
      color === "warning" && "text-warning",
      color === "error" && "text-error",
      color === "default" && "text-default",
      "hover:underline",
      "hover:brightness-70",
      "dark:text-white"
    ],
    (disabled || isLoading) && ["opacity-50", "cursor-not-allowed"],
    fullWidth && "w-full",
    className
  );

  return (
    <button className={baseCls} type={type} disabled={disabled || isLoading} {...rest}>
      {isLoading ? <LoaderOnButton /> : rest.children}
    </button>
  );
}
